
import React, { useState, useEffect, useCallback } from 'react';
import { X, Upload, RefreshCw, FileSpreadsheet, AlertCircle, CheckCircle } from 'lucide-react';
import { readCsvFile, parseCsv } from '../../utils/csv';
import { api } from '../../services/api';
import { Client } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  type: 'auto_other' | 'recurring';
  onSuccess: () => void;
}

function robustNormalize(input: string) {
    if (!input) return '';
    let normalized = input;
    normalized = normalized.replace(/[Ａ-Ｚａ-ｚ０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xFEE0));
    normalized = normalized.toUpperCase();
    const noise = ['株式会社', '有限会社', '合同会社', '合資会社', '一般社団法人', '財団法人', '（株）', '（有）', '(株)', '(ย)'];
    const noiseRegExp = new RegExp(noise.join('|'), 'g');
    normalized = normalized.replace(noiseRegExp, '');
    normalized = normalized.replace(/[-\s\u3000\(\)（）\uFF0Dー・./,，、。]/g, '');
    return normalized;
}

const RuleUploadModal: React.FC<Props> = ({ isOpen, onClose, type, onSuccess }) => {
  const [file, setFile] = useState<File | null>(null);
  const [encoding, setEncoding] = useState<'shift-jis' | 'utf-8'>('shift-jis');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<string | null>(null);
  const [allClients, setAllClients] = useState<Client[]>([]);

  useEffect(() => {
    if (isOpen) {
        setFile(null);
        setPreviewData([]);
        setError(null);
        setSuccessInfo(null);
        setEncoding('shift-jis');
        api.clients.getAll().then(setAllClients);
    }
  }, [isOpen]);

  const cleanNumber = (val: any): number => {
    if (typeof val === 'number') return val;
    if (!val) return 0;
    const cleaned = String(val).replace(/[¥,円\s]/g, '').replace(/[▲△－ー\-]/g, '-');
    return parseInt(cleaned) || 0;
  };

  const processFile = useCallback(async (selectedFile: File, currentEncoding: 'shift-jis' | 'utf-8') => {
    setIsProcessing(true);
    setError(null);
    try {
      const text = await readCsvFile(selectedFile, currentEncoding);
      const rows = parseCsv(text);
      if (rows.length <= 1) throw new Error("データ行が見つかりません。");

      const dataRows = rows.slice(1);
      setHeaders(['得意先CD', '得意先名', '振込人名', '金額', 'マスタ照合']);
      
      const parsed = dataRows.map((row) => {
          const codeFromFile = row[1]?.trim() || '';
          const nameFromFile = row[2]?.trim() || '';
          const kanaFromFile = row[4]?.trim() || '';
          const amount = cleanNumber(row[5]);
          const notes = row[6]?.trim() || '';

          if (!codeFromFile && !nameFromFile && !kanaFromFile) return null;

          let matchedClient = allClients.find(c => c.client_code === codeFromFile);
          if (!matchedClient) {
              const targetName = robustNormalize(nameFromFile);
              const targetKana = robustNormalize(kanaFromFile);
              matchedClient = allClients.find(c => {
                  const mName = robustNormalize(c.client_name);
                  const mKana = robustNormalize(c.client_kana);
                  return (targetName && mName === targetName) || (targetKana && mKana === targetKana);
              });
          }

          return {
              // 修正: '未設定'という文字列ではなく、空文字またはundefinedにする
              client_code: matchedClient ? matchedClient.client_code : (codeFromFile || ''),
              client_name: matchedClient ? matchedClient.client_name : (nameFromFile || '不明'),
              remitter_name: kanaFromFile || nameFromFile,
              amount: amount,
              expected_amount: amount,
              notes: notes,
              status_label: matchedClient ? '一致' : '新規データ'
          };
      }).filter(r => r !== null);
      
      setPreviewData(parsed);
    } catch(e:any) {
        setError(e.message);
    } finally {
        setIsProcessing(false);
    }
  }, [allClients]);

  useEffect(() => {
      if (file && allClients.length > 0) processFile(file, encoding);
  }, [encoding, file, allClients, processFile]);

  const executeRegister = async () => {
      setIsProcessing(true);
      setError(null);
      try {
          if (previewData.length === 0) throw new Error("登録可能なデータがありません。");
          for (const rule of previewData) {
              const { status_label, ...ruleToSave } = rule;
              if (type === 'recurring') await api.clients.saveRecurringRule(ruleToSave);
              else await api.clients.saveAutoRule(ruleToSave);
          }
          setSuccessInfo(`${previewData.length}件のルールを登録しました。`);
          onSuccess();
          setTimeout(onClose, 2000);
      } catch(e:any) { setError("登録失敗: " + e.message); }
      finally { setIsProcessing(false); }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-0 flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in duration-200">
        <div className="px-6 py-4 border-b flex justify-between items-center bg-gray-50">
            <h2 className="text-[15px] font-bold text-gray-800">一括登録プレビュー</h2>
            <button onClick={onClose} className="text-gray-400 p-1"><X size={20} /></button>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col p-6 space-y-4">
            <div className="bg-blue-50 border border-blue-100 p-3 rounded-lg flex items-start gap-3 text-[13px] text-blue-800">
                <AlertCircle size={16} className="mt-0.5" />
                <p className="font-bold">全件を登録対象とします。マスタに一致しない行はそのままの内容でルールが作成されます。</p>
            </div>

            {!file ? (
                <div 
                    className="flex-1 border-2 border-dashed rounded-xl flex flex-col items-center justify-center p-8 border-gray-300 bg-gray-50 hover:bg-white cursor-pointer"
                    onClick={() => document.getElementById('rule-file')?.click()}
                >
                    <Upload size={40} className="text-gray-300 mb-4" />
                    <p className="text-gray-500 font-bold text-sm">CSVファイルを選択またはドロップ</p>
                    <input id="rule-file" type="file" className="hidden" accept=".csv" onChange={(e) => e.target.files?.[0] && setFile(e.target.files[0])} />
                </div>
            ) : (
                <div className="flex-1 flex flex-col overflow-hidden border rounded-xl bg-white">
                    <div className="bg-gray-50 px-4 py-2 border-b flex justify-between items-center text-[11px] text-gray-400 font-black">
                        <span>{file.name} / 解析：{previewData.length} 件</span>
                        <button onClick={() => setFile(null)} className="text-blue-600">再選択</button>
                    </div>
                    <div className="flex-1 overflow-auto">
                        <table className="w-full text-[13px] text-left whitespace-nowrap">
                            <thead className="bg-gray-100 sticky top-0 z-10 shadow-sm">
                                <tr>{headers.map((h, i) => <th key={i} className="p-3 border-b font-black text-gray-600">{h}</th>)}</tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {isProcessing ? (
                                    <tr><td colSpan={5} className="p-10 text-center"><RefreshCw className="animate-spin mx-auto text-blue-400" /></td></tr>
                                ) : previewData.map((row, idx) => (
                                    <tr key={idx} className="hover:bg-gray-50">
                                        <td className="p-3 font-mono font-bold text-blue-600 text-[13px]">{row.client_code || <span className="text-gray-300">未設定</span>}</td>
                                        <td className="p-3 font-bold text-[13px]">{row.client_name}</td>
                                        <td className="p-3 text-gray-800 font-bold text-[12px]">{row.remitter_name}</td>
                                        <td className="p-3 text-right font-black text-[13px]">¥{(row.amount || row.expected_amount || 0).toLocaleString()}</td>
                                        <td className="p-3 text-center">
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${row.status_label === '一致' ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                                {row.status_label}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            
            <div className="flex items-center gap-4 bg-gray-50 p-2.5 rounded-lg border">
                <span className="text-[11px] font-black text-gray-400">文字コード：</span>
                <select value={encoding} onChange={(e) => setEncoding(e.target.value as any)} className="text-[13px] border rounded p-1 font-bold">
                    <option value="shift-jis">Shift-JIS</option>
                    <option value="utf-8">UTF-8</option>
                </select>
            </div>

            {successInfo && <div className="bg-green-50 text-green-700 p-3 rounded-lg border border-green-200 text-[13px] font-bold flex items-center gap-2 animate-in slide-in-from-bottom-1"><CheckCircle size={14} /> {successInfo}</div>}
            {error && <div className="bg-red-50 text-red-700 p-3 rounded-lg border border-red-200 text-[13px] font-bold flex items-center gap-2"><AlertCircle size={14} /> {error}</div>}
        </div>
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
            <button onClick={onClose} className="px-5 py-2 rounded-lg border text-gray-600 font-bold hover:bg-white text-[13px]">キャンセル</button>
            <button 
                onClick={executeRegister} 
                disabled={!file || previewData.length === 0 || isProcessing || !!successInfo} 
                className="px-10 py-2 rounded-lg bg-blue-600 text-white font-black hover:bg-blue-700 disabled:opacity-50 text-[13px] shadow-md transition-all active:scale-95 flex items-center gap-2"
            >
                {isProcessing && <RefreshCw size={14} className="animate-spin" />}
                全件を登録する（{previewData.length}件）
            </button>
        </div>
      </div>
    </div>
  );
};
export default RuleUploadModal;
